import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    num: '01',
    title: 'Free Consultation',
    desc: 'We assess your vehicle, home, or commercial space and recommend the ideal film type and shade based on your needs for heat rejection, privacy, and style.',
    icon: 'ri-search-eye-line',
  },
  {
    num: '02',
    title: 'Surface Preparation',
    desc: 'Windows are thoroughly cleaned and decontaminated to remove all dust, oils, and debris — ensuring flawless adhesion and a bubble-free installation.',
    icon: 'ri-drop-line',
  },
  {
    num: '03',
    title: 'Precision Film Cutting',
    desc: 'Film is cut to exact measurements using computer-precision templates for your specific vehicle make, model, window dimensions, or building glass.',
    icon: 'ri-scissors-cut-line',
  },
  {
    num: '04',
    title: 'Expert Film Installation',
    desc: 'Our trained technicians install the tint film using professional-grade tools and heat-shrinking techniques for a perfect, seamless fit with zero bubbles.',
    icon: 'ri-paint-brush-line',
  },
  {
    num: '05',
    title: 'Final Inspection',
    desc: 'Every window is inspected post-installation for quality, clarity, and adhesion. We don\'t release a vehicle or project until we\'re fully satisfied with the result.',
    icon: 'ri-shield-check-line',
  },
];

export default function ProcessSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-16 overflow-hidden"
      ref={ref}
      style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #111111 50%, #0a0a0a 100%)' }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[300px] bg-[#AAFF00]/3 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div
          className={`text-center mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[1px] bg-[#AAFF00]" />
            <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">Our Process</span>
            <div className="w-8 h-[1px] bg-[#AAFF00]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl xl:text-6xl font-bold text-white mb-5">
            How We Install<br />
            <em className="not-italic text-[#AAFF00]">Your Window Film</em>
          </h2>
          <p className="text-white/45 font-sans text-base max-w-md mx-auto">
            Five precision steps — executed without shortcuts — to deliver flawless results that last for years.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-10 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#AAFF00]/20 to-transparent" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {steps.map((step, i) => (
              <div
                key={step.num}
                className={`relative group transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="relative flex justify-start lg:justify-center mb-6">
                  <div className="w-20 h-20 rounded-full border border-[#AAFF00]/20 bg-[#AAFF00]/5 flex items-center justify-center group-hover:border-[#AAFF00]/50 group-hover:bg-[#AAFF00]/10 transition-all duration-500">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className={`${step.icon} text-[#AAFF00] text-2xl`}></i>
                    </div>
                  </div>
                  <span className="absolute -top-2 -right-1 lg:right-auto font-serif text-[10px] font-bold text-[#AAFF00]/60 tracking-widest bg-[#0a0a0a] px-1">
                    {step.num}
                  </span>
                </div>
                <div className="lg:text-center">
                  <h3 className="font-serif text-white text-lg font-bold mb-2">{step.title}</h3>
                  <p className="text-white/45 text-xs font-sans leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`mt-16 text-center transition-all duration-700 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <a
            href="#estimate"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-8 py-4 rounded-full hover:bg-[#C4FF33] transition-all duration-300 cursor-pointer"
          >
            Book Your Appointment
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
